/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2024 (original work) Open Assessment Technologies SA ;
 */

/**
 * Convert pixels to rem.
 * If the rem size is not known, it will be retrieved.
 * @param {Number} valueInPixels - the value to convert as a number
 * @param {Number} [remSize] - the size of 1rem, if not set it will be calculated
 * @returns {Number} the value converted to rem
 * @throws {TypeError} if the input value is not a number
 */
export function pxToRem(valueInPixels, remSize) {
    if (typeof valueInPixels !== 'number' || Number.isNaN(valueInPixels)) {
        throw new TypeError('Only numbers are accepted as pixels');
    }

    remSize = remSize || getRemSizePx();

    return valueInPixels / remSize;
}

/**
 * Convert rems to pixels.
 * If the rem size is not known, it will be retrieved.
 * @param {Number} valueInRem - the value to convert as a number
 * @param {Number} [remSize] - the size of 1rem, if not set it will be calculated
 * @returns {Number} the value converted to pixels
 * @throws {TypeError} if the input value is not a number
 */
export function remToPx(valueInRem, remSize) {
    if (typeof valueInRem !== 'number' || Number.isNaN(valueInRem)) {
        throw new TypeError('Only numbers are accepted as rems');
    }

    remSize = remSize || getRemSizePx();

    return remSize * valueInRem;
}

/**
 * Returns calculated rem size in pixels with fallback to value of 16
 * @returns {Number} the pixel size of 1 rem
 */
export function getRemSizePx() {
    return parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
}

/**
 * Returns default rem size in pixels, set by default css styles in ui-identity
 * @returns {Number} the pixel size of 1 rem
 */
export function getDefaultRemSizePx() {
    return 8;
}
